import { Cross1Icon } from "@radix-ui/react-icons";
import { CalendarRange, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasSimpleSelect,
  CanvasTextArea,
  FieldSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  DateBetweenProps,
  DateBetweenShape,
  dateBetweenType,
  dateOperators,
  Dimensions,
  Field,
  HourOfDayBetweenProps,
  HourOfDayBetweenShape,
  hourOfDayBetweenType,
  hourOptions,
  metadataType,
  TimeWindowBounds,
  timestampFieldOptions,
  utcOffsetOptions,
} from "@/components/canvas-editor/shapes/shapes";
import {
  availableZones,
  browserZone,
  composeBound,
  dateWindowProblems,
  decomposeBound,
  hourWindowProblems,
  parseSchedule,
  ScheduleZone,
  WindowProblem,
} from "@/components/canvas-editor/shapes/time-window-schedule";
import {
  MIN_H,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { ReadOnlyValue } from "@/components/hooks/ListContainer";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { ListItem, UnorderedList } from "@/components/ui/typography";

function timestampField(value: string): Field {
  return (timestampFieldOptions.find((f) => f.value === value)?.value ??
    timestampFieldOptions[0]!.value) as Field;
}

/** Both time nodes start on ARRIVAL — when the post reached the network, not when its author
 *  says they wrote it.
 *
 * A feed author reported a 10pm-4am feed admitting afternoon posts. `createdAt` is written by
 * the posting client and validated by nothing upstream, and bridged accounts are routinely
 * hours out: 40.9% of `*.brid.gy` posts sit more than ten minutes from their arrival, p95
 * 4.2h, against 0.70% of everything else. Arrival is also the timestamp the Bluesky app
 * displays, so defaulting to `createdAt` made a feed and the app disagree about the same post
 * — which reads as a broken feed rather than as a field set the wrong way.
 *
 * ONE constant for both nodes, on purpose. #112 split this in two so Date Range could keep
 * `createdAt`, on the argument that a date range is often about a post's own dating. That was
 * overruled: an author picking dates means the dates they saw the posts on, and two sibling
 * nodes disagreeing about what "the date" means is its own confusion. Both fields stay
 * selectable on both nodes, and the picker hint says which is which — so the narrower case
 * (archiving what was WRITTEN in a period, catching a backdated post) is still one click away.
 *
 * Existing nodes are untouched: a loaded shape takes its field from the manifest
 * (`build-manifest.ts` reads `hour_of_day_between[0]` / `date_between[0]`), so this only
 * decides where a NEWLY placed node starts.
 */
const DEFAULT_TIME_FIELD = timestampField("arrived_at");

// 6pm to 9pm: the window the request that prompted this node asked for.
const DEFAULT_HOUR_WINDOW: TimeWindowBounds = { start: "18", end: "21" };
const EMPTY_DATE_WINDOW: TimeWindowBounds = { start: "", end: "" };

const hourLabel = (hour: string) =>
  hourOptions.find((o) => o.value === hour)?.label ?? hour;

const windowsValidator = T.arrayOf(T.object({ start: T.string, end: T.string }));

function replaceWindow(
  windows: TimeWindowBounds[],
  index: number,
  patch: Partial<TimeWindowBounds>,
): TimeWindowBounds[] {
  return windows.map((w, i) => (i === index ? { ...w, ...patch } : w));
}

function removeWindow(
  windows: TimeWindowBounds[],
  index: number,
): TimeWindowBounds[] {
  return windows.filter((_, i) => i !== index);
}

/** A zoned bound's suffix as the label the offset picker uses ("UTC-04:00", "UTC"). */
function zoneLabel(zone: string): string {
  if (zone === "Z" || zone === "+00:00") return "UTC";
  return `UTC${zone}`;
}

function RemoveWindowButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <CanvasButton
      size="smIcon"
      variant="ghost"
      aria-label={label}
      className="pointer-events-auto opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
      onPress={onPress}
    >
      <Cross1Icon />
    </CanvasButton>
  );
}

function AnyWindowCaption({ count }: { count: number }) {
  if (count < 2) return null;
  return (
    <span className="text-slate-dim text-xs px-1">
      matches any of these windows
    </span>
  );
}

/** The first few problems, by row, in the words the save gate will use. */
function summarizeProblems(problems: WindowProblem[]): string {
  const shown = problems.slice(0, 3).map((p) => p.message);
  const more = problems.length - shown.length;
  return more > 0 ? `${shown.join(" ")} And ${more} more.` : shown.join(" ");
}

/**
 * Why this node matches nothing right now, in red under its rows.
 *
 * Both engines fail CLOSED on one unusable window -- the whole node answers false --
 * and neither can say which window. So the canvas says it, in the row's own words,
 * for as long as the problem is there. Shown in read-only views too: a published feed
 * carrying a broken node is exactly where a reader needs the sentence.
 */
function WindowProblems({ problems }: { problems: WindowProblem[] }) {
  if (problems.length === 0) return null;
  return (
    <span className="text-xs text-red-11" role="alert">
      This node matches no posts until fixed: {summarizeProblems(problems)}
    </span>
  );
}

/**
 * The hook into the feed form's save gate.
 *
 * The form saves through native constraint validation: on submit, the browser fires
 * `invalid` on the first control that fails, and FeedForm's handler zooms the canvas to
 * the node that owns it and toasts "fix them before saving". `required` catches an
 * empty input, but "range 3 ends before it starts" is a fact about two inputs and no
 * attribute expresses it -- `setCustomValidity` does. This is a focusable,
 * visually-hidden input inside the node that carries that message, and clears it the
 * moment the windows are fixed. Nothing is submitted from it.
 */
function ValiditySentinel({ message }: { message: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.setCustomValidity(message);
  }, [message]);
  // NOT `readOnly`: a read-only input is barred from constraint validation, so its
  // custom message would never block a submit (checked in Storybook, 2026-09-16).
  return (
    <input
      ref={ref}
      className="sr-only"
      tabIndex={-1}
      aria-hidden="true"
      value=""
      onChange={() => {}}
    />
  );
}

export function isHourOfDayBetweenShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is HourOfDayBetweenShape {
  return shape.type === hourOfDayBetweenType;
}

export function isDateBetweenShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is DateBetweenShape {
  return shape.type === dateBetweenType;
}

// --- Time of Day -----------------------------------------------------------------------

export class HourOfDayBetweenTool extends ToolStateNode {
  static override id = hourOfDayBetweenType;

  static override uiToolDef = (editor: Editor) => ({
    id: hourOfDayBetweenType,
    icon: Clock,
    label: HourOfDayBetweenShapeUtil.docs.title,
    defaultColor: HourOfDayBetweenShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(hourOfDayBetweenType),
    docs: () => (
      <NodeDocs
        editor={editor}
        util={HourOfDayBetweenShapeUtil}
        component={HourOfDayBetween}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;
    this.editor.createShape({
      type: hourOfDayBetweenType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });
    this.editor.setCurrentTool("select");
  }
}

export class HourOfDayBetweenShapeUtil extends BindableUtil<HourOfDayBetweenShape> {
  static override migrations = createMigrations(hourOfDayBetweenType);
  static override type = hourOfDayBetweenType;

  static override docs = {
    defaultColor: "indigo" as const,
    title: "Time of Day",
    description: (
      <div>
        <div>Matches posts made between two hours of the day.</div>
        <UnorderedList className="ml-1">
          <ListItem className="my-1">
            Use it for “posts made in the evening”, any day — something{" "}
            <span className="font-black">after</span> and{" "}
            <span className="font-black">before</span> cannot express, because
            they compare whole moments in time rather than the time of day.
          </ListItem>
          <ListItem className="my-1">
            The <span className="font-black">end hour is not included</span>, so
            6pm to 9pm is the three hours 6, 7 and 8. Two windows that meet never
            both claim the same hour.
          </ListItem>
          <ListItem className="my-1">
            A window may <span className="font-black">cross midnight</span> — 10pm
            to 2am is the four hours you would expect.
          </ListItem>
          <ListItem className="my-1">
            A node may hold <span className="font-black">many windows</span>{" "}
            and matches a post that falls in <span className="font-black">any</span>{" "}
            of them — “mornings and evenings” is one node, not two.
          </ListItem>
          <ListItem className="my-1">
            The timezone is your audience’s, not the poster’s. It is a fixed
            offset, so it does not shift for daylight saving.
          </ListItem>
          <ListItem className="my-1">
            A post whose timestamp cannot be read is excluded rather than let
            through.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">Which date you pick matters.</span>{" "}
            <span className="font-black">Post Created Date</span> is what the
            posting app claims; <span className="font-black">Post Arrival Date</span>{" "}
            is when the post actually reached the network, and is the timestamp
            the Bluesky app shows you. They usually agree within a second or two,
            but bridged accounts — anything ending{" "}
            <span className="font-black">.brid.gy</span> — are routinely hours
            apart, which is what makes a feed look like it let the wrong posts in.
          </ListItem>
        </UnorderedList>
      </div>
    ),
  };

  override getType() {
    return hourOfDayBetweenType;
  }

  static override props: RecordProps<HourOfDayBetweenShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    windows: windowsValidator,
    utcOffsetMinutes: T.string,
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 620,
      h: MIN_H,
      var: DEFAULT_TIME_FIELD,
      windows: [DEFAULT_HOUR_WINDOW],
      utcOffsetMinutes: "0",
      metadata: undefined,
    };
  }

  override component(shape: HourOfDayBetweenShape) {
    const selected = this.editor.getSelectedShapeIds();
    return (
      <HourOfDayBetween
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={selected.includes(shape.id)}
      />
    );
  }
}

function HourOfDayBetween({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: HourOfDayBetweenShape;
  className?: string;
  updateShape: (
    shape: HourOfDayBetweenShape,
    props: Partial<HourOfDayBetweenProps>,
  ) => void;
}) {
  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => updateShape(shape, dimensions),
    [shape, updateShape],
  );
  const { windows } = shape.props;
  const problems = hourWindowProblems(windows);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          HourOfDayBetweenShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={HourOfDayBetweenShapeUtil.docs}
        />
        <BlockBody>
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <SimpleTooltip message="Which timestamp on the post to read.">
                <FieldSelect
                  value={shape.props.var}
                  validOperators={dateOperators}
                  onValueChange={(v) => updateShape(shape, { var: v as Field })}
                />
              </SimpleTooltip>
              <span className="text-slate-dim px-1">in</span>
              <SimpleTooltip message="Your audience's timezone, as a fixed offset. It does not shift for daylight saving.">
                <CanvasSimpleSelect
                  aria-label="Timezone"
                  value={shape.props.utcOffsetMinutes}
                  options={utcOffsetOptions}
                  onValueChange={(v) =>
                    updateShape(shape, { utcOffsetMinutes: v })
                  }
                />
              </SimpleTooltip>
              <AnyWindowCaption count={windows.length} />
            </div>

            {isReadOnly ? (
              <div className="flex flex-col gap-1">
                {windows.map((w, index) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <ReadOnlyValue key={`${shape.id}-window-${index}`}>
                    between {hourLabel(w.start)} and {hourLabel(w.end)}
                  </ReadOnlyValue>
                ))}
                <WindowProblems problems={problems} />
              </div>
            ) : (
              <>
                {windows.map((w, index) => (
                  <div
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={`${shape.id}-window-${index}`}
                    className="group flex items-center gap-2"
                  >
                    <span className="text-slate-dim px-1">between</span>
                    <SimpleTooltip message="The first hour included.">
                      <CanvasSimpleSelect
                        aria-label={`Start hour ${index + 1}`}
                        value={w.start}
                        options={hourOptions}
                        onValueChange={(v) =>
                          updateShape(shape, {
                            windows: replaceWindow(windows, index, { start: v }),
                          })
                        }
                      />
                    </SimpleTooltip>
                    <span className="text-slate-dim px-1">and</span>
                    <SimpleTooltip message="The first hour NOT included — 6pm to 9pm is three hours.">
                      <CanvasSimpleSelect
                        aria-label={`End hour ${index + 1}`}
                        value={w.end}
                        options={hourOptions}
                        onValueChange={(v) =>
                          updateShape(shape, {
                            windows: replaceWindow(windows, index, { end: v }),
                          })
                        }
                      />
                    </SimpleTooltip>
                    {windows.length > 1 && (
                      <RemoveWindowButton
                        label={`Remove window ${index + 1}`}
                        onPress={() =>
                          updateShape(shape, {
                            windows: removeWindow(windows, index),
                          })
                        }
                      />
                    )}
                  </div>
                ))}
                <CanvasButton
                  size="sm"
                  variant="ghost"
                  className="self-start"
                  onPress={() =>
                    updateShape(shape, {
                      windows: [...windows, DEFAULT_HOUR_WINDOW],
                    })
                  }
                >
                  Add another window
                </CanvasButton>
                <WindowProblems problems={problems} />
                <ValiditySentinel message={summarizeProblems(problems)} />
              </>
            )}
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}

// --- Date Range ------------------------------------------------------------------------

export class DateBetweenTool extends ToolStateNode {
  static override id = dateBetweenType;

  static override uiToolDef = (editor: Editor) => ({
    id: dateBetweenType,
    icon: CalendarRange,
    label: DateBetweenShapeUtil.docs.title,
    defaultColor: DateBetweenShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(dateBetweenType),
    docs: () => (
      <NodeDocs
        editor={editor}
        util={DateBetweenShapeUtil}
        component={DateBetween}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;
    this.editor.createShape({
      type: dateBetweenType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });
    this.editor.setCurrentTool("select");
  }
}

export class DateBetweenShapeUtil extends BindableUtil<DateBetweenShape> {
  static override migrations = createMigrations(dateBetweenType);
  static override type = dateBetweenType;

  static override docs = {
    defaultColor: "indigo" as const,
    title: "Date Range",
    description: (
      <div>
        <div>Matches posts made within a range of dates.</div>
        <UnorderedList className="ml-1">
          <ListItem className="my-1">
            Put it inside a logic block to date-limit{" "}
            <span className="font-black">part</span> of a feed rather than the
            whole thing.
          </ListItem>
          <ListItem className="my-1">
            The <span className="font-black">end date is not included</span>, so
            two ranges that meet never both claim the same day. To cover all of
            the 2nd, end on the 3rd.
          </ListItem>
          <ListItem className="my-1">
            The timezone decides where a day begins — “the 2nd” in UTC starts on
            the evening of the 1st in the Americas.
          </ListItem>
          <ListItem className="my-1">
            A node may hold <span className="font-black">many ranges</span> and
            matches a post that falls in <span className="font-black">any</span>{" "}
            of them — a whole season of games is one node.
          </ListItem>
          <ListItem className="my-1">
            A bound may include a <span className="font-black">time of day</span>.
            Leave the time empty for midnight, so a range of whole days needs only
            dates.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">Paste a schedule</span> accepts one
            window per line — “2026-10-22 7:00pm to 10:30pm”, or a date alone for
            the whole day. When a named timezone is chosen, each time is stored
            with its own offset, so a season that crosses a daylight-saving change
            stays right game by game; the node’s timezone then applies only to
            dates and times entered without one.
          </ListItem>
          <ListItem className="my-1">
            A post whose timestamp cannot be read is excluded rather than let
            through.
          </ListItem>
          <ListItem className="my-1">
            <span className="font-black">Which date you pick matters.</span>{" "}
            <span className="font-black">Post Created Date</span> is what the
            posting app claims; <span className="font-black">Post Arrival Date</span>{" "}
            is when the post actually reached the network, and is the timestamp
            the Bluesky app shows you. They usually agree within a second or two,
            but bridged accounts — anything ending{" "}
            <span className="font-black">.brid.gy</span> — are routinely hours
            apart, which is what makes a feed look like it let the wrong posts in.
          </ListItem>
        </UnorderedList>
      </div>
    ),
  };

  override getType() {
    return dateBetweenType;
  }

  static override props: RecordProps<DateBetweenShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    windows: windowsValidator,
    utcOffsetMinutes: T.string,
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 720,
      h: MIN_H,
      var: DEFAULT_TIME_FIELD,
      windows: [EMPTY_DATE_WINDOW],
      utcOffsetMinutes: "0",
      metadata: undefined,
    };
  }

  override component(shape: DateBetweenShape) {
    const selected = this.editor.getSelectedShapeIds();
    return (
      <DateBetween
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={selected.includes(shape.id)}
      />
    );
  }
}

/**
 * One bound of a Date Range window: its leading word, a date, an optional time,
 * and a note when it is zoned. One inline group, so a row that is too wide wraps
 * BETWEEN the two bounds rather than orphaning a time input on its own line.
 */
function DateBoundInputs({
  bound,
  index,
  side,
  onChange,
}: {
  bound: string;
  index: number;
  side: "start" | "end";
  onChange: (bound: string) => void;
}) {
  const parts = decomposeBound(bound);
  const which = side === "start" ? "Start" : "End";
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-slate-dim px-1">
        {side === "start" ? "from" : "until"}
      </span>
      <SimpleTooltip
        message={
          side === "start"
            ? "The first day included."
            : "The first day NOT included. To cover all of the 2nd, end on the 3rd."
        }
      >
        <CanvasInput
          aria-label={`${which} date ${index + 1}`}
          type="date"
          className="w-[9.5rem] shrink-0"
          value={parts.date}
          onValueChange={(date) => onChange(composeBound({ ...parts, date }))}
          // Required (the CanvasInput default), so an empty date is caught by the
          // form's own save gate. An empty range once saved as `["", ""]` and took
          // the whole node dark; see WindowProblems. The TIME below stays optional:
          // empty there means midnight.
        />
      </SimpleTooltip>
      <SimpleTooltip message="Optional. Empty means midnight.">
        <CanvasInput
          aria-label={`${which} time ${index + 1}`}
          type="time"
          className="w-[7.5rem] shrink-0"
          value={parts.time}
          onValueChange={(time) => onChange(composeBound({ ...parts, time }))}
          required={false}
        />
      </SimpleTooltip>
      {parts.zone && (
        <SimpleTooltip message="This bound carries its own offset, so the node's timezone does not move it.">
          <span className="text-slate-dim text-xs">{zoneLabel(parts.zone)}</span>
        </SimpleTooltip>
      )}
    </span>
  );
}

/**
 * "Paste a schedule": one window per line, read in a chosen zone. The parse runs
 * as the author types so an unreadable line is named before anything is added;
 * "Add" appends the windows in the order given and clears the text.
 */
function PasteSchedule({
  nodeOffsetLabel,
  onAdd,
}: {
  nodeOffsetLabel: string;
  onAdd: (windows: TimeWindowBounds[]) => void;
}) {
  const [text, setText] = useState("");
  const [zoneValue, setZoneValue] = useState(() => browserZone());
  const zoneOptions = useMemo(
    () => [
      { value: "node", label: `This node's timezone (${nodeOffsetLabel})` },
      ...availableZones().map((zone) => ({ value: zone, label: zone })),
    ],
    [nodeOffsetLabel],
  );
  const parsed = useMemo(() => {
    const zone: ScheduleZone =
      zoneValue === "node"
        ? { kind: "node" }
        : { kind: "iana", zone: zoneValue };
    return parseSchedule(text, zone);
  }, [text, zoneValue]);
  const count = parsed.windows.length;

  return (
    <div className="flex flex-col gap-2 p-2 rounded-md border border-slate-normal bg-slate-subtle/50">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-slate-dim px-1">Times are in</span>
        <SimpleTooltip message="A named zone stores each time with the offset it has on that date, so a schedule across a daylight-saving change stays right. The node's timezone stores plain times that the node's offset applies to.">
          <CanvasSimpleSelect
            aria-label="Schedule timezone"
            value={zoneValue}
            options={zoneOptions}
            onValueChange={setZoneValue}
          />
        </SimpleTooltip>
      </div>
      <CanvasTextArea
        aria-label="Schedule, one window per line"
        minRows={4}
        className="min-h-[96px] font-mono text-xs"
        placeholder={
          "One window per line, for example:\n2026-10-22 7:00pm to 10:30pm\n2026-10-24 19:00 - 01:30\n2026-10-26"
        }
        value={text}
        onValueChange={setText}
        required={false}
      />
      {parsed.errors.length > 0 && (
        <span className="text-xs text-red-11">
          Could not read line{parsed.errors.length > 1 ? "s" : ""}{" "}
          {parsed.errors.join(", ")}. Nothing was added.
        </span>
      )}
      <CanvasButton
        size="sm"
        variant="outline"
        className="self-start"
        disabled={count === 0}
        onPress={() => {
          onAdd(parsed.windows);
          setText("");
        }}
      >
        Add {count} {count === 1 ? "window" : "windows"}
      </CanvasButton>
    </div>
  );
}

function DateBetween({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: DateBetweenShape;
  className?: string;
  updateShape: (
    shape: DateBetweenShape,
    props: Partial<DateBetweenProps>,
  ) => void;
}) {
  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const [pasteOpen, setPasteOpen] = useState(false);
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => updateShape(shape, dimensions),
    [shape, updateShape],
  );
  const { windows } = shape.props;
  const problems = dateWindowProblems(
    windows,
    Number(shape.props.utcOffsetMinutes),
  );
  const nodeOffsetLabel =
    utcOffsetOptions.find((o) => o.value === shape.props.utcOffsetMinutes)
      ?.label ?? `UTC${shape.props.utcOffsetMinutes}`;

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color || DateBetweenShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={DateBetweenShapeUtil.docs} />
        <BlockBody>
          <div className="flex-1 flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <SimpleTooltip message="Which timestamp on the post to read.">
                <FieldSelect
                  value={shape.props.var}
                  validOperators={dateOperators}
                  onValueChange={(v) => updateShape(shape, { var: v as Field })}
                />
              </SimpleTooltip>
              <span className="text-slate-dim px-1">in</span>
              <SimpleTooltip message="Which timezone dates and times without their own offset are counted in. A fixed offset; it does not shift for daylight saving.">
                <CanvasSimpleSelect
                  aria-label="Timezone"
                  value={shape.props.utcOffsetMinutes}
                  options={utcOffsetOptions}
                  onValueChange={(v) =>
                    updateShape(shape, { utcOffsetMinutes: v })
                  }
                />
              </SimpleTooltip>
              <AnyWindowCaption count={windows.length} />
            </div>

            {isReadOnly ? (
              <div className="flex flex-col gap-1">
                {windows.map((w, index) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <ReadOnlyValue key={`${shape.id}-window-${index}`}>
                    {w.start || "…"} – {w.end || "…"}
                  </ReadOnlyValue>
                ))}
                <WindowProblems problems={problems} />
              </div>
            ) : (
              <>
                {windows.map((w, index) => (
                  <div
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={`${shape.id}-window-${index}`}
                    className="group flex items-center gap-2 flex-wrap"
                  >
                    <DateBoundInputs
                      bound={w.start}
                      index={index}
                      side="start"
                      onChange={(start) =>
                        updateShape(shape, {
                          windows: replaceWindow(windows, index, { start }),
                        })
                      }
                    />
                    <DateBoundInputs
                      bound={w.end}
                      index={index}
                      side="end"
                      onChange={(end) =>
                        updateShape(shape, {
                          windows: replaceWindow(windows, index, { end }),
                        })
                      }
                    />
                    {windows.length > 1 && (
                      <RemoveWindowButton
                        label={`Remove range ${index + 1}`}
                        onPress={() =>
                          updateShape(shape, {
                            windows: removeWindow(windows, index),
                          })
                        }
                      />
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <CanvasButton
                    size="sm"
                    variant="ghost"
                    onPress={() =>
                      updateShape(shape, {
                        windows: [...windows, EMPTY_DATE_WINDOW],
                      })
                    }
                  >
                    Add another range
                  </CanvasButton>
                  <CanvasButton
                    size="sm"
                    variant="ghost"
                    aria-expanded={pasteOpen}
                    onPress={() => setPasteOpen((open) => !open)}
                  >
                    {pasteOpen ? "Close schedule" : "Paste a schedule"}
                  </CanvasButton>
                </div>
                {pasteOpen && (
                  <PasteSchedule
                    nodeOffsetLabel={nodeOffsetLabel}
                    onAdd={(added) =>
                      updateShape(shape, {
                        // A pasted schedule FILLS the empty rows rather than sitting
                        // under them. A new node starts with one empty range, and
                        // appending below it left that blank at the top of an 80-game
                        // list, where it took the whole node dark (2026-09-15).
                        windows: [
                          ...windows.filter((w) => w.start || w.end),
                          ...added,
                        ],
                      })
                    }
                  />
                )}
                <WindowProblems problems={problems} />
                <ValiditySentinel message={summarizeProblems(problems)} />
              </>
            )}
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
